/**
 * Mastodon API client - Timelines endpoints
 */

import { get, buildQuery } from "./http";
import type { Status, TimelineParams, PaginationParams } from "./types";

export type TimelineType = "home" | "public" |  "local" | "federated" | "hashtag" | "list" | "bookmarks" | "favourites";

/**
 * Get home timeline (statuses from followed accounts)
 */
export async function getHomeTimeline(params?: TimelineParams): Promise<Status[]> {
	const query = params ? buildQuery(params) : "";
	return get<Status[]>(`/api/v1/timelines/home${query}`);
}

/**
 * Get public timeline
 */
export async function getPublicTimeline(
	params?: TimelineParams & { local?: boolean; remote?: boolean },
): Promise<Status[]> {
	const query = params ? buildQuery(params) : "";
	return get<Status[]>(`/api/v1/timelines/public${query}`);
}

/**
 * Get local timeline (public timeline with local=true)
 */
export async function getLocalTimeline(params?: TimelineParams): Promise<Status[]> {
	return getPublicTimeline({ ...params, local: true });
}

/**
 * Get federated timeline (public timeline with remote=true)
 */
export async function getFederatedTimeline(params?: TimelineParams): Promise<Status[]> {
	return getPublicTimeline({ ...params, remote: true });
}

/**
 * Get hashtag timeline
 */
export async function getHashtagTimeline(
	params: TimelineParams & {
		hashtag: string;
		any?: string[];
		all?: string[];
		none?: string[];
	},
): Promise<Status[]> {
	const { hashtag, ...queryParams } = params;
	const query = queryParams ? buildQuery(queryParams) : "";
	return get<Status[]>(`/api/v1/timelines/tag/${encodeURIComponent(hashtag)}${query}`);
}

/**
 * Get list timeline
 */
export async function getListTimeline(
	params: PaginationParams & { listId: string },
): Promise<Status[]> {
	const { listId, ...queryParams } = params;
	const query = queryParams ? buildQuery(queryParams) : "";
	return get<Status[]>(`/api/v1/timelines/list/${listId}${query}`);
}

/**
 * Get bookmarked statuses
 */
export async function getBookmarks(params?: PaginationParams): Promise<Status[]> {
	const query = params ? buildQuery(params) : "";
	return get<Status[]>(`/api/v1/bookmarks${query}`);
}

/**
 * Get favourited statuses
 */
export async function getFavourites(params?: PaginationParams): Promise<Status[]> {
	const query = params ? buildQuery(params) : "";
	return get<Status[]>(`/api/v1/favourites${query}`);
}

export const timelines = {
	home: getHomeTimeline,
	public: getPublicTimeline,
	local: getLocalTimeline,
	federated: getFederatedTimeline,
	hashtag: getHashtagTimeline,
	list: getListTimeline,
	bookmarks: getBookmarks,
	favourites: getFavourites,
};
